import { useEffect } from 'react';

/**
 * The page behind a modal must not scroll, and two modals must not fight over
 * saying so.
 *
 * The palette and the detail sheet each used to save `body.style.overflow`,
 * write `hidden`, and put back what they saved. That is correct on its own and
 * wrong together, and the two do overlap: on a phone the palette is how you find
 * the task, and picking one opens the sheet while the palette is still up. The
 * sheet saves `hidden` (the palette's), the palette restores `''`, and then the
 * sheet's own cleanup writes back the `hidden` it saved -- onto a page with no
 * modal over it and no way left to scroll.
 *
 * So the lock is counted, in one module: the first holder saves the real value
 * and hides, the last one puts the real value back, and any number in between
 * cost nothing. The count is module state rather than a context because it is
 * about the document, and there is only one of those.
 */
let holders = 0;
let saved = '';

export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    if (holders === 0) {
      saved = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    holders += 1;
    return () => {
      holders -= 1;
      if (holders === 0) document.body.style.overflow = saved;
    };
  }, [active]);
}
